import React, { useCallback, useEffect, useRef, useState } from "react";
import { Select, Spin, Alert } from "antd";
import { faStopwatch } from "@fortawesome/free-solid-svg-icons";
import { useSelector } from "react-redux";
// COMPONENTS
import DrawerRowDivider from "../../components/drawer/DrawerRowDivider";
// REDUX
import { useLazyGetBankQuery } from "../../../redux/banks/bankAPI";
// AUTH
import GETJwtToken from "../../../redux/authentication/authentication";
// CONTEXT
import { useWizard } from "../WizardContext";
import { WizardOpGate } from "../WizardStepGate";
// WIZARD UTILS
import { WizardStep } from "../utils/wizardInvalidation";
import { useWizardStepDraft } from "../utils/useWizardStepDraft";
import { useWizardStepRefresh } from "../utils/useWizardStepEntry";
import { describeError } from "../utils/describeError";
// INTERFACES
import { BankInterface } from "../../../interfaces/Bank.interface";
// DEBOUNCE OVERRIDES
import BanksDebounceOverrides from "../../edgeManagement/banksConfiguration/BanksDebounceOverrides";
// STORE
import { RootState } from "../../../app/store";
// FLATBUFFERS
import { formatUuidT } from "../../../flatbuffers/display";

/**
 * Compares a freshly fetched bank against what's already in state, so a
 * re-entry refetch that comes back unchanged doesn't hand
 * BanksDebounceOverrides a new object reference for no reason.
 *
 * Scoped to id/channelId/adapterId/orgId/type: the UuidT fields are exactly
 * the ones wizardInvalidation.ts's BANK_FIELD_RULES/ADAPTER_FIELD_RULES/
 * CHANNEL_FIELD_RULES already declare as Debounce-invalidating, plus the two
 * others BanksDebounceOverrides itself keys its own fetch/submit payload on
 * (id, orgId). Compared via formatUuidT's string form — the same helper
 * BanksDebounceOverrides.tsx already uses for this exact need — rather than
 * object identity, since every fetch deserializes fresh UuidT instances
 * regardless of whether the value changed. `type` is included because
 * BanksDebounceOverrides sends it verbatim in that same submit payload.
 */
function hasBankChanged(previous: BankInterface | null, next: BankInterface): boolean {
  if (!previous) {
    return true;
  }
  return (
    formatUuidT(previous.id) !== formatUuidT(next.id) ||
    formatUuidT(previous.channelId) !== formatUuidT(next.channelId) ||
    formatUuidT(previous.adapterId) !== formatUuidT(next.adapterId) ||
    formatUuidT(previous.orgId) !== formatUuidT(next.orgId) ||
    previous.type !== next.type
  );
}

export default function StepDebounce() {
  const { theme } = useSelector((state: RootState) => state.home);
  const { state: wizardState, markStepCompleted } = useWizard();
  const { bankIds, bankNames } = wizardState;

  const [selectedBankId, setSelectedBankId] = useState<string | null>(null);
  const [bankData, setBankData] = useState<BankInterface | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [triggerGetBank, { isFetching }] = useLazyGetBankQuery();

  // Default to the first bank on mount, when bankIds changes, or — the
  // second half of this condition — when the current selection no longer
  // names a bank in bankIds at all (deleted out from under the operator
  // while they were elsewhere in the wizard). Without that second half, a
  // deleted bank stays "selected" forever: this effect used to only fill in
  // a selection when there was none, so selectedBankId and the stale
  // bankData it produced would linger indefinitely with no way back to a
  // valid state. Clearing bankData/loadError on repoint mirrors the
  // dropdown's own onChange handler below, so the switch doesn't flash the
  // deleted bank's now-irrelevant panel under the fetch spinner.
  useEffect(() => {
    if (bankIds.length === 0) {
      return;
    }
    if (!selectedBankId || !bankIds.includes(selectedBankId)) {
      setSelectedBankId(bankIds[0]);
      setBankData(null);
      setLoadError(null);
    }
  }, [bankIds, selectedBankId]);

  // ************************************************ */
  // DRAFT PERSISTENCE ****************************** */
  // Persists which bank the operator was editing so a reload returns
  // them to the same row in the BanksDebounceOverrides editor. The
  // per-bank override values themselves are server-side state in the
  // bank record (saved via PUT /banks), so this draft only needs to
  // hold the selection — no override values pass through here.
  //
  // Verification: only persist when the selectedBankId actually points
  // at a bank in the current wizard's bank list. A stale id from a
  // previous wizard run would just be ignored.
  useWizardStepDraft<{ selectedBankId: string | null }>({
    orgId: wizardState.organizationId,
    step: WizardStep.Debounce,
    scopeId: "debounce",
    scopeType: "step",
    draft: { selectedBankId },
    onRestore: (saved) => {
      if (typeof saved.selectedBankId === "string" && bankIds.includes(saved.selectedBankId)) {
        setSelectedBankId(saved.selectedBankId);
      }
    },
    validate: (snapshot) =>
      snapshot.selectedBankId !== null && bankIds.includes(snapshot.selectedBankId),
  });

  // Supersession token for the bank fetch. Two call sites reach fetchBank —
  // the selection-change effect below and the step-entry refresh further
  // down — so "is this response still wanted?" has to outlive any single
  // effect's cleanup. Every call bumps the token and captures it; a response
  // whose captured token is no longer current is dropped rather than written
  // over newer state. Matching on bankId alone is not sufficient: both call
  // sites can legitimately fire for the same bank (a selection change and an
  // invalidated re-entry landing in one commit), and a bankId match would
  // let the older of those two responses win regardless of arrival order.
  const bankFetchTokenRef = useRef(0);

  // Whether the current selection still names a real bank, as a plain
  // boolean rather than a bankIds-mirroring ref: bankIds gets a new array
  // reference on every bank create/delete anywhere in the wizard (see
  // StepBanks.tsx's setState calls), and steps stay mounted, so a ref mirror
  // is not needed to avoid recreating fetchBank on unrelated bankIds churn —
  // a boolean dependency already collapses to "same value" via Object.is
  // regardless of which new bankIds array produced it. fetchBank's identity
  // (and therefore the effect below) only changes when membership actually
  // flips, exactly as intended, with no ref and no per-render mutation.
  const selectedBankExists = selectedBankId !== null && bankIds.includes(selectedBankId);

  /**
   * Loads the full bank record that backs the debounce overrides panel.
   * Two callers: the selection-change effect immediately below, and the
   * step-entry refresh after it. Both route through here so the
   * supersession token, the error text, and the error-clearing all live in
   * one place.
   *
   * Guards on `selectedBankExists` as well as `!selectedBankId` — the
   * default-selection effect above re-points a selection that no longer
   * names a bank in `bankIds`, but that re-point lands in ITS OWN render,
   * one tick after `bankIds` itself changes. In the render in between,
   * `selectedBankId` still names the just-deleted bank while `bankIds` no
   * longer contains it — exactly the window `selectedBankExists` closes,
   * so a step-entry refresh landing in that window does not GET a bank
   * that no longer exists, paint a new error banner, and leave the child
   * rendering that dead bank's grid with a still-live Save button. It does
   * clear a stale `loadError` on its way out, though: a bank being deleted
   * moots whatever load failure was previously shown for it, and the
   * guard's silent no-op would otherwise leave that stale message on
   * screen until the re-point's own fetch (if any) succeeds.
   */
  const fetchBank = useCallback(async (): Promise<void> => {
    setLoadError(null);
    if (!selectedBankId || !selectedBankExists) {
      return;
    }
    bankFetchTokenRef.current += 1;
    const requestToken = bankFetchTokenRef.current;
    const isStale = () => requestToken !== bankFetchTokenRef.current;
    let token: string;
    try {
      token = await GETJwtToken();
    } catch (err) {
      // GETJwtToken's Cognito path redirects to login on failure, so this
      // was previously safe to swallow there — but the OFFLINE build's
      // refreshOfflineToken() can reject with no cached fallback and no
      // redirect at all. That failure needs the same signal any other load
      // failure gets, not a silent, unexplained blank panel.
      if (isStale()) {
        return;
      }
      setLoadError(`Failed to load bank debounce configuration — ${describeError(err)}`);
      return;
    }
    try {
      const result = await triggerGetBank({ bankId: selectedBankId, token }).unwrap();
      if (isStale() || !result?.data) {
        return;
      }
      const freshBank = result.data as BankInterface;
      setBankData((current) => (hasBankChanged(current, freshBank) ? freshBank : current));
      setLoadError(null);
    } catch (err) {
      if (isStale()) {
        return;
      }
      setLoadError(`Failed to load bank debounce configuration — ${describeError(err)}`);
    }
  }, [selectedBankId, selectedBankExists, triggerGetBank]);

  // Fetch full bank object when selected bank changes
  useEffect(() => {
    fetchBank();
  }, [fetchBank]);

  // Re-read the bank record when an upstream edit invalidated this step.
  // wizardInvalidation.ts flags Debounce stale when a bank's channelId or
  // adapterId changes (BANK_FIELD_RULES), when its adapter's protocolType,
  // serverId or organizationId changes (ADAPTER_FIELD_RULES), or when the
  // channel's serverId or organizationId changes (CHANNEL_FIELD_RULES).
  //
  // adapterId is the one that bites in practice: BanksDebounceOverrides
  // fetches its key list from bank.adapterId, so without this the operator
  // edits a debounce grid built from the OLD adapter's message keys after
  // changing the bank's adapter — a live staleness defect, not a
  // hypothetical one. Refetching hands the child fresh bank identity, which
  // re-runs its own key/override fetch and rebuilds the grid; that cascade
  // is the repair, not a side effect.
  //
  // A rule-table invalidation doesn't always mean THIS bank's id/adapterId
  // actually changed value, though — e.g. a sibling channel's serverId
  // changing also invalidates Debounce, without touching this bank's own
  // fields at all. Unlike the Select Bank picker's onChange (which nulls
  // bankData immediately, because it's a guaranteed real bank change),
  // fetchBank swaps bankData in place with no intermediate null — so it
  // goes through hasBankChanged (above) rather than writing every response
  // unconditionally: bankAPI's keepUnusedDataFor: 0 means every fetch
  // deserializes fresh UuidT instances regardless of whether the value
  // changed, and BanksDebounceOverrides' own effect is keyed on
  // bank.id/bank.adapterId — handing it a new reference for a same-value
  // response would still trigger its cascade and rebuild its grid out from
  // under an in-progress edit. Comparing first keeps the refetch itself
  // unconditional and cheap (one GET) while only disturbing the child when
  // something about THIS bank genuinely changed. Contrast StepBacnet, which
  // deliberately does NOT use this hook — see the note on its loadAdapter
  // effect.
  useWizardStepRefresh({
    step: WizardStep.Debounce,
    refetch: fetchBank,
  });

  // Auto-mark this step complete whenever the operator is on it.
  // Debounce overrides are optional — the operator may have nothing
  // to configure, or may have set overrides on a previous visit that
  // already persisted to the bank record server-side. Either way
  // Next should never be gated on a click here.
  //
  // The hardcoded `markStepCompleted(6)` that lived here previously
  // referred to the OLD step indices and accidentally marked Lifts
  // complete instead of Debounce when ChannelModifiers was inserted
  // at index 5 — using the enum pins the right step regardless of
  // future reordering. Depending on `currentStep` re-fires this if
  // an upstream step unmarked us.
  useEffect(() => {
    if (wizardState.currentStep === WizardStep.Debounce) {
      markStepCompleted(WizardStep.Debounce);
    }
  }, [wizardState.currentStep, markStepCompleted]);

  const bankOptions = bankIds.map((id) => ({
    value: id,
    label: bankNames[id] || id,
  }));

  return (
    <Spin spinning={isFetching}>
      <div style={{ padding: "16px" }}>
        {loadError && (
          <Alert
            type="error"
            showIcon
            closable
            message={loadError}
            onClose={() => setLoadError(null)}
            style={{ marginBottom: 16 }}
          />
        )}

        <DrawerRowDivider icon={faStopwatch} title="Debounce Configuration" />

        {bankIds.length === 0 ? (
          <div className={`drawer__box__${theme}`} style={{ padding: "16px" }}>
            <Alert
              type="warning"
              message="No banks configured yet. Complete the Banks step first."
              showIcon
            />
          </div>
        ) : (
          <>
            <div className={`drawer__box__${theme}`} style={{ padding: "16px" }}>
              <div className="container">
                <div className="row">
                  <div className="col-12">
                    <span className="generalStyles__inputLabel">
                      <span className="generalStyles__inputFlexRequired">*</span>
                      Select Bank
                    </span>
                    <Select
                      style={{ width: "100%", marginTop: 4 }}
                      value={selectedBankId ?? undefined}
                      onChange={(val) => {
                        setSelectedBankId(val);
                        setBankData(null);
                      }}
                      options={bankOptions}
                      placeholder="Select a bank"
                      size="large"
                    />
                  </div>
                </div>
              </div>
            </div>

            {bankData && (
              <>
                <br />
                <DrawerRowDivider icon={faStopwatch} title="Debounce Overrides" />
                <div className={`drawer__box__${theme}`} style={{ padding: "16px" }}>
                  {/* The shared grid's one Save posts, puts and deletes overrides together. */}
                  <WizardOpGate op="update">
                    <BanksDebounceOverrides bank={bankData} isLoading={false} />
                  </WizardOpGate>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Spin>
  );
}
